import { useState } from 'react'
import type { ReactNode } from 'react'
import { createFileRoute } from '@tanstack/react-router'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faCheck,
  faChevronDown,
  faBolt,
  faTag,
  faBuilding,
  faEnvelope,
  faFlag,
  faFileInvoice,
  faBoxesStacked,
  faSchool,
  faTruckFast,
  faPlay,
  faCamera,
} from '@fortawesome/free-solid-svg-icons'
import { faWhatsapp } from '@fortawesome/free-brands-svg-icons'
import {
  getProductPrimaryImageUrl,
  getStoreContactEmail,
  getStoreContactPhone,
  getStorefrontConfig,
  getStorefrontProduct,
  listStorefrontTestimonials,
  toExternalImage,
} from '../lib/dropify'
import { INDEXABLE_ROBOTS, absoluteUrl, brandTitle, buildFaqSchema, DEFAULT_OG_IMAGE } from '../lib/seo'
import type { FaqItem } from '../lib/seo'
import { whatsAppCtaProps, track } from '../lib/analytics'
import { listDashboardNewsPosts } from '../lib/news'
import { BrandTrademark, SocialLinks } from '../components/SiteChrome'
import { WhatsAppChannelSection } from '../components/WhatsAppChannelSection'
import { AdNewsProofSection } from '../components/HomeNewsSection'
import { LpCrossLinks } from '../components/LpCrossLinks'

const WA_NUMBER = '6285704011028'

const PRODUCT_NAME = 'Sarung Tangan Paskibra'

// The dedicated paskibra testimonial in the dashboard: warehouse photos and
// videos of the actual glove bales (marked per seri/ukuran) being packed and
// loaded. The proof section below is built from exactly this entry.
const PROOF_TESTIMONIAL_ID = '682bce26-5033-40aa-b88f-2f010aa74149'

function waLink(message: string) {
  return `https://wa.me/${WA_NUMBER}?text=${encodeURIComponent(message)}`
}

type ProofMedia = { type: 'image' | 'video'; url: string }

// The API wraps image URLs in a plain /unsafe/ thumbor pass-through; unwrap to
// the raw source so we can request a properly sized rendition instead.
function rawMediaUrl(url: string): string {
  const marker = '/unsafe/'
  const at = url.indexOf(marker)
  return at >= 0 ? url.slice(at + marker.length) : url
}

// Pull the media of one specific testimonial out of the list response. The
// detail endpoint 404s for testimonials, so we filter the list by id.
function extractProofMedia(raw: unknown, testimonialId: string): ProofMedia[] {
  const list = Array.isArray(raw)
    ? raw
    : raw && typeof raw === 'object' && Array.isArray((raw as Record<string, unknown>).items)
      ? (raw as { items: unknown[] }).items
      : []
  for (const entry of list) {
    if (!entry || typeof entry !== 'object') continue
    const record = entry as Record<string, unknown>
    if (record.id !== testimonialId || !Array.isArray(record.media_items)) continue
    const media: Array<ProofMedia & { sort: number }> = []
    for (const item of record.media_items) {
      if (!item || typeof item !== 'object') continue
      const m = item as Record<string, unknown>
      const url = typeof m.url === 'string' ? m.url : null
      if (!url) continue
      media.push({
        type: m.media_type === 'video' ? 'video' : 'image',
        url,
        sort: typeof m.sort_order === 'number' ? m.sort_order : media.length,
      })
    }
    return media.sort((a, b) => a.sort - b.sort).map(({ type, url }) => ({ type, url }))
  }
  return []
}

// This buyer stocks paskibra gloves in bulk: school-supply shops, koperasi
// sekolah, seragam/atribut stores, pembina paskibra, and event committees.
// The FAQ answers wholesale, sizing, season-deadline, and admin questions.
const FAQ_ITEMS: FaqItem[] = [
  {
    q: 'Berapa harga grosir sarung tangan paskibra di Dropify?',
    a: 'Harga mengikuti merek, model, ukuran, dan jumlah pesanan. Kirim pilihan warna, komposisi ukuran, jumlah, serta kota tujuan agar admin dapat menyiapkan quotation.',
  },
  {
    q: 'Berapa harga kaos tangan paskibra putih?',
    a: 'Harga grosir kaos tangan paskibra putih mengikuti jumlah pesanan, seri, dan komposisi ukuran. Minta daftar harga terbaru via WhatsApp dengan menyebutkan seri (LUPO atau OREO) dan jumlah lusin yang dibutuhkan.',
  },
  {
    q: 'Minimal ordernya berapa?',
    a: 'MOQ sarung tangan umumnya mulai 25 lusin atau 300 pasang. MOQ kaos kaki umumnya 50 lusin. Konfirmasikan ketentuan campur ukuran dan model sebelum meminta quotation.',
    link: { href: '/minimum-order', label: 'Lihat detail minimum order per kategori →' },
  },
  {
    q: 'Ada ukuran untuk SD, SMP, dan SMA?',
    a: 'Ada. Sarung Tangan Paskibra OREO S123 dan Kaos Kaki Pramuka TUYAYA memiliki pilihan ukuran SD, SMP, dan SMA. Kirim jumlah untuk setiap ukuran agar admin dapat memeriksa komposisinya sebelum order.',
  },
  {
    q: 'Warna apa yang tersedia?',
    a: 'Untuk sarung tangan paskibra tersedia putih (LUPO SU999P dan OREO S123) dan hitam (LUPO SU999H). Putih untuk pasukan pengibar bendera dan acara formal, hitam biasanya untuk variasi seragam, petugas upacara, dan kebutuhan formal lainnya.',
  },
  {
    q: 'Bagaimana memeriksa kenyamanan untuk latihan dan upacara?',
    a: 'Periksa bahan, jahitan, panjang jari, dan kelonggaran pada sample. Uji pada ukuran siswa yang berbeda sebelum menentukan komposisi pesanan satu pasukan.',
  },
  {
    q: 'Bisa dikirim sebelum 17 Agustus?',
    a: 'Bisa. Kirim target penerimaan, jumlah, dan komposisi ukuran sejak awal. Admin akan memeriksa stok, waktu penyiapan, dan estimasi perjalanan sebelum pesanan dikonfirmasi.',
  },
  {
    q: 'Saya pengurus koperasi sekolah atau panitia. Apakah bisa memakai PO dan meminta invoice?',
    a: 'Bisa. Sekolah, koperasi, atau instansi dapat memakai PO dan meminta invoice untuk kebutuhan administrasi. Sampaikan dokumen yang diperlukan saat meminta quotation.',
  },
  {
    q: 'Saya ingin menjual produknya kembali. Apakah bisa?',
    a: 'Bisa. Toko perlengkapan sekolah dan reseller dapat membeli dalam jumlah grosir. Minta quotation, lalu hitung margin setelah harga beli, ongkir, kemasan, dan biaya kanal penjualan.',
  },
  {
    q: 'Bisa minta sample dulu sebelum order besar?',
    a: 'Bisa. Hubungi admin untuk memeriksa ketersediaan sample, biaya, dan pengirimannya. Tim Anda dapat menilai bahan, ukuran, dan jahitan sebelum memesan dalam jumlah besar.',
  },
  {
    q: 'Kirim ke luar Jawa bisa?',
    a: 'Bisa. Kirim kota tujuan dan target penerimaan. Admin akan membandingkan layanan ekspedisi dan mengirim nomor resi setelah paket diserahkan untuk pengiriman.',
  },
]

// The four real storefront products this page sells, fetched by slug in the
// loader so each card carries its actual photo and product link.
const LP_PRODUCT_SLUGS = {
  lupoPutih: 'sarung-tangan-paskibra-lupo-su999p',
  lupoHitam: 'sarung-tangan-paskibra-lupo-su999h',
  oreo: 'sarung-tangan-praktis-s123',
  kaosKaki: 'kaos-kaki-pramuka-tuyaya-p001h-hitam',
} as const

export const Route = createFileRoute('/sarung-tangan-paskibra')({
  loader: async () => {
    // Config failure falls back to null: the contact helpers carry hardcoded
    // defaults, and paid traffic must never land on a 500 because the
    // dashboard API hiccupped.
    const [config, testimonialsRaw, lupoPutih, lupoHitam, oreo, kaosKaki, newsPosts] = await Promise.all([
      getStorefrontConfig().catch(() => null),
      listStorefrontTestimonials({ page: 1, per_page: 50 }).catch(() => []),
      fetchLpProductCached(LP_PRODUCT_SLUGS.lupoPutih),
      fetchLpProductCached(LP_PRODUCT_SLUGS.lupoHitam),
      fetchLpProductCached(LP_PRODUCT_SLUGS.oreo),
      fetchLpProductCached(LP_PRODUCT_SLUGS.kaosKaki),
      listDashboardNewsPosts().catch(() => []),
    ])

    return {
      config,
      proofMedia: extractProofMedia(testimonialsRaw, PROOF_TESTIMONIAL_ID),
      products: { lupoPutih, lupoHitam, oreo, kaosKaki },
      newsPosts,
    }
  },
  head: () => {
    const title = brandTitle('Grosir Sarung Tangan Paskibra Putih & Hitam SD-SMA')
    const description =
      'Sarung tangan paskibra putih dan hitam untuk kebutuhan sekolah serta instansi. MOQ 25 lusin. Periksa ukuran, stok, quotation, dan waktu penyiapan melalui WhatsApp.'

    return {
      // Indexable: targets organic buyers searching "grosir sarung tangan
      // paskibra" and can also serve as a paid-ads destination.
      meta: [
        { title },
        { name: 'description', content: description },
        { name: 'robots', content: INDEXABLE_ROBOTS },
        { property: 'og:title', content: title },
        { property: 'og:description', content: description },
        { property: 'og:url', content: absoluteUrl('/sarung-tangan-paskibra') },
        { property: 'og:image', content: DEFAULT_OG_IMAGE },
        { name: 'twitter:card', content: 'summary_large_image' },
        { name: 'twitter:title', content: title },
        { name: 'twitter:description', content: description },
        { name: 'twitter:image', content: DEFAULT_OG_IMAGE },
      ],
      links: [{ rel: 'canonical', href: absoluteUrl('/sarung-tangan-paskibra') }],
      scripts: [
        {
          type: 'application/ld+json',
          children: JSON.stringify(buildFaqSchema(FAQ_ITEMS)),
        },
        {
          type: 'application/ld+json',
          children: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'ItemList',
            name: 'Sarung tangan paskibra dan kaos kaki pramuka di Dropify',
            itemListElement: PASKIBRA_SCHEMA_PRODUCTS,
          }),
        },
      ],
    }
  },
  component: PaskibraLandingPage,
})

// Product schema mirrors the showcase cards; prices stay out because the page
// routes pricing to WhatsApp quotations.
const PASKIBRA_SCHEMA_PRODUCTS = [
  { name: 'Sarung Tangan Paskibra LUPO SU999P', slug: LP_PRODUCT_SLUGS.lupoPutih, brand: 'LUPO' },
  { name: 'Sarung Tangan Paskibra LUPO SU999H', slug: LP_PRODUCT_SLUGS.lupoHitam, brand: 'LUPO' },
  { name: 'Sarung Tangan Paskibra OREO S123', slug: LP_PRODUCT_SLUGS.oreo, brand: 'OREO' },
  { name: 'Kaos Kaki Pramuka TUYAYA P001H', slug: LP_PRODUCT_SLUGS.kaosKaki, brand: 'TUYAYA' },
].map((product, index) => ({
  '@type': 'ListItem',
  position: index + 1,
  item: {
    '@type': 'Thing',
    name: product.name,
    url: absoluteUrl(`/produk/${product.slug}`),
    brand: { '@type': 'Brand', name: product.brand },
  },
}))

// A real storefront product resolved in the loader, so each showcase card
// shows an actual photo and links to its product page. `rawImage` keeps the
// untransformed URL so the hero can request a larger thumbor size.
type LpProduct = { name: string; slug: string; image: string | null; rawImage: string | null }

const PRODUCT_IMAGE_TRANSFORM = { width: 480, format: 'webp', quality: 70 } as const
const HERO_IMAGE_TRANSFORM = { width: 800, format: 'webp', quality: 75 } as const

// dropifyFetch deliberately never caches /storefront/products, but these
// showcase lookups don't need per-request freshness. Cache them at the route
// level so ad clicks don't pay four API round-trips of TTFB. Failed lookups
// retry sooner so a transient API error doesn't hide the cards for an hour.
const LP_PRODUCT_TTL_MS = 60 * 60 * 1000
const LP_PRODUCT_MISS_TTL_MS = 5 * 60 * 1000
const lpProductCache = new Map<string, { expiresAt: number; product: LpProduct | null }>()

async function fetchLpProductCached(slug: string): Promise<LpProduct | null> {
  const cached = lpProductCache.get(slug)
  if (cached && cached.expiresAt > Date.now()) return cached.product
  let product: LpProduct | null = null
  try {
    const record = await getStorefrontProduct(slug)
    const url = getProductPrimaryImageUrl(record)
    product = {
      name: record.name,
      slug: record.slug || record.id,
      image: url ? toExternalImage(url, PRODUCT_IMAGE_TRANSFORM) : null,
      rawImage: url,
    }
  } catch {
    product = null
  }
  const ttlMs = product ? LP_PRODUCT_TTL_MS : LP_PRODUCT_MISS_TTL_MS
  lpProductCache.set(slug, { expiresAt: Date.now() + ttlMs, product })
  return product
}

// Amber highlighter swipe behind the hook keyword — the page's signature.
function Mark({ children }: { children: ReactNode }) {
  return (
    <span className="relative whitespace-nowrap">
      <span className="relative z-10">{children}</span>
      <span aria-hidden className="absolute -inset-x-1 bottom-0.5 z-0 h-[0.5em] -rotate-1 rounded-sm bg-amber-400/80" />
    </span>
  )
}

const WHY_US_ITEMS = [
  {
    icon: faTag,
    title: 'Quotation sesuai susunan',
    desc: 'Quotation merinci merek, model, ukuran, jumlah, harga, dan ongkir sebelum pembayaran.',
  },
  {
    icon: faBoxesStacked,
    title: 'Susunan ukuran diperiksa',
    desc: 'Kirim jumlah sarung tangan putih atau hitam untuk setiap ukuran. Admin akan memeriksa stok sebelum membuat quotation.',
  },
  {
    icon: faFileInvoice,
    title: 'PO dan invoice',
    desc: 'Pengadaan sekolah, koperasi, atau instansi bisa pakai Purchase Order dan invoice resmi.',
  },
  {
    icon: faTruckFast,
    title: 'Pengiriman antarkota',
    desc: 'Waktu penyiapan dikonfirmasi oleh admin. Resi dikirim setelah paket diserahkan kepada ekspedisi.',
  },
]

const FIT_ITEMS = [
  'Toko perlengkapan sekolah, seragam, atau atribut pramuka yang menyimpan stok sarung tangan paskibra.',
  'Koperasi sekolah yang menyediakan sarung tangan dan kaos kaki untuk kegiatan upacara.',
  'Pembina paskibra atau pihak sekolah yang memerlukan susunan ukuran untuk satu pasukan.',
  'Panitia paskibraka atau dinas yang menangani pengadaan perlengkapan pasukan pengibar bendera.',
  'Penjual online yang menjual perlengkapan sekolah dan atribut upacara.',
  'Konveksi atau vendor seragam yang menerima pesanan seragam beserta atributnya.',
]

type ShowcaseItem = {
  image: string | null
  title: string
  desc: string
  tag: string
  href: string
}

// Copy claims mirror the storefront product descriptions: LUPO = kuat,
// elastis, menyerap keringat; OREO & TUYAYA = tersedia ukuran SD/SMP/SMA.
function buildShowcaseItems(products: {
  lupoPutih: LpProduct | null
  lupoHitam: LpProduct | null
  oreo: LpProduct | null
  kaosKaki: LpProduct | null
}): ShowcaseItem[] {
  const items: ShowcaseItem[] = []

  if (products.lupoPutih) {
    items.push({
      image: products.lupoPutih.image,
      href: `/produk/${products.lupoPutih.slug}`,
      title: 'Sarung Tangan Paskibra LUPO SU999P',
      desc: 'Sarung tangan putih seri LUPO SU999P untuk kebutuhan paskibra. Periksa bahan, jahitan, dan ukuran pada sample sebelum menyusun pesanan satu pasukan.',
      tag: 'LUPO · Putih',
    })
  }

  if (products.lupoHitam) {
    items.push({
      image: products.lupoHitam.image,
      href: `/produk/${products.lupoHitam.slug}`,
      title: 'Sarung Tangan Paskibra LUPO SU999H',
      desc: 'Sarung tangan hitam seri LUPO SU999H untuk variasi seragam dan petugas upacara. Periksa bahan, jahitan, dan ukurannya sebelum memesan.',
      tag: 'LUPO · Hitam',
    })
  }

  if (products.oreo) {
    items.push({
      image: products.oreo.image,
      href: `/produk/${products.oreo.slug}`,
      title: 'Sarung Tangan Paskibra OREO S123',
      desc: 'Sarung tangan putih OREO S123 tersedia dalam ukuran SD, SMP, dan SMA. Periksa ukuran pada sample atau size chart sebelum memesan untuk satu pasukan.',
      tag: 'OREO · SD-SMA',
    })
  }

  if (products.kaosKaki) {
    items.push({
      image: products.kaosKaki.image,
      href: `/produk/${products.kaosKaki.slug}`,
      title: 'Kaos Kaki Pramuka TUYAYA P001H',
      desc: 'Kaos kaki pramuka hitam ukuran SD, SMP, dan SMA. Pelengkap wajib etalase perlengkapan sekolah, sekalian kulakan bareng sarung tangannya.',
      tag: 'TUYAYA · Kaos kaki',
    })
  }

  return items
}

// Quick-compare table: concrete, extractable facts (warna, ukuran, use case)
// for featured snippets and AI answers. Claims mirror the showcase cards.
const SPEC_ROWS = [
  {
    seri: 'LUPO SU999P',
    warna: 'Putih',
    ukuran: 'Standar',
    cocok: 'Paskibra, upacara, acara formal',
  },
  {
    seri: 'LUPO SU999H',
    warna: 'Hitam',
    ukuran: 'Standar',
    cocok: 'Petugas upacara & kebutuhan formal',
  },
  {
    seri: 'OREO S123',
    warna: 'Putih',
    ukuran: 'SD, SMP, SMA',
    cocok: 'Sekolah & paskibra semua jenjang',
  },
  {
    seri: 'Kaos Kaki TUYAYA P001H',
    warna: 'Hitam',
    ukuran: 'SD, SMP, SMA',
    cocok: 'Pramuka & seragam sekolah',
  },
]

const OBJECTIONS = [
  {
    worry: '“Kami perlu warna dan model yang seragam untuk satu pasukan.”',
    answer: 'Kirim merek, kode model, warna, ukuran, dan jumlah. Admin akan memeriksa ketersediaan susunan yang sama sebelum quotation dibuat.',
  },
  {
    worry: '“Target penerimaan kami dekat dengan jadwal upacara.”',
    answer: 'Kirim target penerimaan, jumlah, dan ukuran. Admin akan memeriksa stok, waktu penyiapan, serta estimasi perjalanan sebelum Anda memesan.',
  },
  {
    worry: '“Ukuran tangan siswa kan beda-beda.”',
    answer: 'Seri OREO S123 tersedia dalam ukuran SD, SMP, dan SMA. Sebutkan jumlah untuk setiap ukuran saat meminta quotation.',
  },
  {
    worry: '“Harus lewat PO dan butuh invoice buat laporan.”',
    answer: 'Bisa. Pengadaan sekolah, koperasi, dan instansi kami layani pakai Purchase Order dan invoice resmi.',
  },
  {
    worry: '“Mau bandingin harga dulu sama tempat lain.”',
    answer: 'Minta quotation tertulis dengan produk, ukuran, jumlah, dan kota tujuan yang sama agar penawaran dapat dibandingkan secara setara.',
  },
]

function WhatsAppButton({
  message,
  label,
  itemName,
  placement,
  className,
}: {
  message: string
  label: string
  itemName?: string
  placement?: string
  className?: string
}) {
  return (
    <a
      href={waLink(message)}
      {...whatsAppCtaProps('landing_page', {
          cta_placement: placement ?? (itemName ? 'product_card' : 'body_primary'),
          lp_category: PRODUCT_NAME,
          ...(itemName ? { lead_item_name: itemName, items: [{ item_id: itemName, item_name: itemName, item_category: PRODUCT_NAME }] } : {}),
        })}
      target="_blank"
      rel="noopener noreferrer"
      className={className}
    >
      <FontAwesomeIcon icon={faWhatsapp} className="w-5 h-5" />
      {label}
    </a>
  )
}

function PaskibraLandingPage() {
  const { config, proofMedia, products, newsPosts } = Route.useLoaderData()
  const showcaseItems = buildShowcaseItems(products)
  const heroImage = products.lupoPutih?.rawImage
    ? toExternalImage(products.lupoPutih.rawImage, HERO_IMAGE_TRANSFORM)
    : null

  const contactPhone = getStoreContactPhone(config)
  const contactEmail = getStoreContactEmail(config)

  const heroMessage = 'Halo Dropify, saya membutuhkan sarung tangan paskibra untuk ___. Pilihan produk: ___. Jumlah per ukuran: ___. Kota tujuan: ___. Mohon siapkan quotation.'
  const softMessage = 'Halo Dropify, saya sedang membandingkan sarung tangan paskibra. Mohon jelaskan pilihan model, ukuran, MOQ, dan ketersediaan sample.'
  const ctaMessage = 'Halo Dropify, saya ingin meminta quotation sarung tangan paskibra. Pilihan produk: ___. Jumlah per ukuran: ___. Kota tujuan: ___. Target penerimaan: ___.'

  return (
    <div className="min-h-screen bg-white">
      {/* Urgency strip */}
      <div className="bg-amber-400 text-slate-900 text-center text-xs sm:text-sm font-semibold px-4 py-2">
        <FontAwesomeIcon icon={faBolt} className="w-3.5 h-3.5 mr-1.5" />
        Kirim jumlah per ukuran dan target penerimaan agar waktu penyiapan dapat diperiksa.
      </div>

      {/* Minimal header — logo + single CTA, no nav links to avoid click leaks */}
      <header className="border-b border-slate-100">
        <div className="max-w-6xl mx-auto px-4 h-16 flex items-center justify-between">
          <a href="/" className="flex items-center gap-2.5" aria-label="Dropify">
            <img src="/logo80.webp" srcSet="/logo40.webp 40w, /logo80.webp 80w" sizes="40px" alt="Dropify" width="40" height="40" className="h-10 w-10 object-contain" />
            <span className="text-lg font-black tracking-tight text-slate-900">Dropify</span>
          </a>
          <WhatsAppButton
            message={heroMessage}
            label="Minta penawaran"
            placement="header"
            className="inline-flex items-center gap-2 rounded-lg bg-green-700 hover:bg-green-800 px-4 py-2.5 text-sm font-bold text-white transition-colors"
          />
        </div>
      </header>

      {/* Hero — hook + dual CTA */}
      <section className="bg-[#0B1120] overflow-hidden">
        <div className="max-w-6xl mx-auto px-4 py-14 md:py-20 grid lg:grid-cols-2 gap-10 lg:gap-12 items-center">
          <div>
            <span className="inline-flex items-center gap-2 rounded-full bg-white/10 px-3 py-1 text-xs font-bold uppercase tracking-wider text-amber-300">
              <FontAwesomeIcon icon={faFlag} className="h-3 w-3" />
              Untuk toko, koperasi sekolah, & panitia
            </span>
            <h1 className="mt-5 text-4xl md:text-5xl font-black tracking-tight text-white leading-[1.08]">
              Grosir sarung tangan paskibra <Mark>untuk sekolah dan instansi.</Mark>
            </h1>
            <p className="mt-5 text-base md:text-lg text-slate-300 leading-relaxed">
              Dropify menyediakan sarung tangan paskibra putih dan hitam dari LUPO serta OREO. OREO S123 tersedia dalam ukuran SD, SMP, dan SMA. Kaos kaki pramuka TUYAYA dapat ditambahkan pada pesanan yang sama setelah MOQ setiap kategori dikonfirmasi.
            </p>

            <div className="mt-6 inline-flex items-baseline gap-2.5 rounded-xl border border-amber-400/30 bg-amber-400/10 px-4 py-3">
              <span className="text-xs font-bold uppercase tracking-wider text-amber-300">Minimum order</span>
              <span className="text-2xl md:text-3xl font-black leading-none text-white">25 lusin</span>
              <span className="text-sm font-medium text-slate-300">300 pasang, cek ketentuan per model</span>
            </div>

            <div className="mt-7 flex flex-col sm:flex-row sm:items-center gap-3">
              <WhatsAppButton
                message={heroMessage}
                label="Minta quotation"
                placement="hero_primary"
                className="inline-flex items-center justify-center gap-2.5 rounded-lg bg-green-700 hover:bg-green-800 px-7 py-4 text-base font-bold text-white shadow-lg shadow-green-900/30 transition-colors"
              />
              <a
                href={waLink(softMessage)}
                {...whatsAppCtaProps('landing_page', { cta_placement: 'hero_secondary', lp_category: PRODUCT_NAME })}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 rounded-lg border border-white/25 px-6 py-4 text-sm font-semibold text-white hover:bg-white/10 transition-colors"
              >
                Bandingkan model dan ukuran →
              </a>
            </div>
            <p className="mt-3 text-sm text-slate-400">Tanyakan ketersediaan dan biaya sample sebelum memesan dalam jumlah besar.</p>
          </div>

          {/* Hero visual — real product photo resolved in the loader */}
          {heroImage && (
            <div className="relative">
              <div className="relative rounded-3xl overflow-hidden ring-1 ring-white/10 shadow-2xl bg-white">
                <img src={heroImage} alt="Sarung tangan paskibra putih LUPO SU999P dari Dropify" width="800" height="800" loading="eager" fetchPriority="high" decoding="async" className="w-full aspect-square object-cover" />
              </div>
              <div className="absolute -bottom-4 -left-3 sm:left-4 flex items-center gap-2 rounded-xl bg-white px-4 py-3 shadow-xl">
                <span className="flex h-9 w-9 items-center justify-center rounded-full bg-amber-400">
                  <FontAwesomeIcon icon={faFlag} className="h-4 w-4 text-slate-900" />
                </span>
                <div className="leading-tight">
                  <p className="text-xs font-black uppercase tracking-wide text-slate-900">Putih & Hitam</p>
                  <p className="text-[11px] text-slate-500">Ukuran SD, SMP, & SMA tersedia</p>
                </div>
              </div>
            </div>
          )}
        </div>
      </section>

      <AdNewsProofSection posts={newsPosts} />

      <WhatsAppChannelSection showSocksPromo={false} />

      {/* Who it's for — let the visitor self-identify */}
      <section className="py-14 bg-white border-b border-slate-100">
        <div className="max-w-6xl mx-auto px-4">
          <div className="mb-9 max-w-2xl">
            <h2 className="text-2xl md:text-3xl font-black tracking-tight text-slate-900">Pembeli yang dilayani halaman ini</h2>
            <p className="mt-2 text-base text-slate-500">Informasi berikut ditujukan untuk sekolah, instansi, panitia, dan toko perlengkapan sekolah yang membeli dalam jumlah grosir.</p>
          </div>
          <div className="grid sm:grid-cols-2 gap-3 md:gap-4">
            {FIT_ITEMS.map((item) => (
              <div key={item} className="flex items-start gap-3 rounded-2xl border border-slate-100 bg-slate-50 p-4">
                <span className="mt-0.5 flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-green-700">
                  <FontAwesomeIcon icon={faCheck} className="h-3 w-3 text-white" />
                </span>
                <p className="text-base leading-relaxed text-slate-700">{item}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Proof section — real warehouse footage of the paskibra glove bales */}
      <ProofSection media={proofMedia} />

      {/* Why us */}
      <section className="py-14 bg-white border-b border-slate-100">
        <div className="max-w-6xl mx-auto px-4">
          <div className="mb-9 max-w-2xl">
            <h2 className="text-2xl md:text-3xl font-black tracking-tight text-slate-900">Kenapa kulakan sarung tangan paskibra di sini?</h2>
            <p className="mt-2 text-base text-slate-500">Empat alasan toko dan koperasi sekolah pilih Dropify sebagai langganan.</p>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
            {WHY_US_ITEMS.map((item) => (
              <div key={item.title} className="rounded-2xl border border-slate-100 bg-slate-50 p-5">
                <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-blue-600">
                  <FontAwesomeIcon icon={item.icon} className="h-5 w-5 text-white" />
                </div>
                <h3 className="mt-4 text-base font-black tracking-tight text-slate-900">{item.title}</h3>
                <p className="mt-1.5 text-sm leading-relaxed text-slate-500">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="py-14 bg-slate-50 border-b border-slate-100">
        <div className="max-w-6xl mx-auto px-4">
          <div className="mb-9 max-w-2xl">
            <h2 className="text-2xl md:text-3xl font-black tracking-tight text-slate-900">Cara ordernya simpel</h2>
            <p className="mt-2 text-base text-slate-500">Empat tahap dari penyampaian kebutuhan hingga pesanan diterima.</p>
          </div>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {[
              { n: '1', title: 'Kirim kebutuhan', desc: 'Sebutkan warna, komposisi ukuran, jumlah, dan target penerimaan.' },
              { n: '2', title: 'Terima quotation', desc: 'Admin mengirim rincian produk, jumlah, harga, ongkir, dan estimasi pengiriman.' },
              { n: '3', title: 'Periksa sample jika diperlukan', desc: 'Sampaikan model dan ukuran yang ingin diperiksa. Admin akan mengonfirmasi ketersediaan serta biaya pengiriman sample.' },
              { n: '4', title: 'Barang dikirim', desc: 'Admin mengonfirmasi waktu penyiapan. Resi dikirim setelah paket diserahkan kepada ekspedisi. Kebutuhan PO dan invoice dapat disampaikan sejak awal.' },
            ].map((step) => (
              <div key={step.n} className="rounded-2xl border border-slate-100 bg-white p-5">
                <span className="flex h-9 w-9 items-center justify-center rounded-full bg-amber-400 text-sm font-black text-slate-900">{step.n}</span>
                <h3 className="mt-4 text-base font-black tracking-tight text-slate-900">{step.title}</h3>
                <p className="mt-1.5 text-sm leading-relaxed text-slate-500">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Product showcase — built from the real storefront products */}
      <section className="py-14 bg-white">
        <div className="max-w-6xl mx-auto px-4">
          <div className="mb-8 max-w-2xl">
            <h2 className="text-2xl md:text-3xl font-black tracking-tight text-slate-900">Sarung tangan paskibra yang kami suplai</h2>
            <p className="mt-2 text-base text-slate-500">Putih dan hitam, ukuran standar sampai SD-SMP-SMA, plus kaos kaki pramuka sebagai pelengkap. Semua siap partai besar.</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            {showcaseItems.map((type) => (
              <div key={type.title} className="group flex flex-col overflow-hidden rounded-2xl border border-slate-100 bg-white transition-all hover:border-blue-200 hover:shadow-lg sm:flex-row">
                {type.image && (
                  <div className="relative overflow-hidden bg-white sm:w-44 sm:flex-shrink-0">
                    <img src={type.image} alt={type.title} width="400" height="400" loading="lazy" decoding="async" className="h-48 w-full object-cover transition-transform duration-500 group-hover:scale-105 sm:h-full" />
                    <span className="absolute left-3 top-3 rounded-full bg-blue-600 px-2.5 py-1 text-[11px] font-bold uppercase tracking-wide text-white shadow">{type.tag}</span>
                  </div>
                )}
                <div className="flex flex-1 flex-col p-5">
                  <h3 className="text-base font-black tracking-tight text-slate-900">{type.title}</h3>
                  <p className="mt-1.5 flex-1 text-base leading-relaxed text-slate-500">{type.desc}</p>
                  <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-2">
                    <WhatsAppButton
                      message={`Halo Dropify, saya butuh ${type.title} partai besar. Boleh minta penawaran harga & info stoknya?`}
                      label="Periksa stok dan quotation"
                      itemName={type.title}
                      className="inline-flex w-fit items-center gap-1.5 rounded-lg bg-green-700 px-4 py-2.5 text-sm font-bold text-white transition-colors hover:bg-green-800"
                    />
                    <a href={type.href} className="text-sm font-semibold text-blue-600 hover:text-blue-700">
                      Lihat detail produk →
                    </a>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Quick-compare spec table */}
          <div className="mt-10">
            <h3 className="text-lg font-black tracking-tight text-slate-900">Perbandingan cepat seri sarung tangan paskibra</h3>
            <div className="mt-4 overflow-x-auto rounded-2xl border border-slate-200">
              <table className="w-full min-w-[640px] text-left text-sm">
                <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
                  <tr>
                    <th className="px-4 py-3 font-bold">Seri / Brand</th>
                    <th className="px-4 py-3 font-bold">Warna</th>
                    <th className="px-4 py-3 font-bold">Ukuran</th>
                    <th className="px-4 py-3 font-bold">Cocok untuk</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {SPEC_ROWS.map((row) => (
                    <tr key={row.seri}>
                      <td className="px-4 py-3 font-semibold text-slate-900">{row.seri}</td>
                      <td className="px-4 py-3 text-slate-600">{row.warna}</td>
                      <td className="px-4 py-3 text-slate-600">{row.ukuran}</td>
                      <td className="px-4 py-3 text-slate-600">{row.cocok}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="mt-3 text-sm text-slate-500">
              MOQ sarung tangan umumnya mulai 25 lusin atau 300 pasang. MOQ kaos kaki umumnya 50 lusin. Konfirmasikan ketentuan per model dan susunan ukuran kepada admin.
            </p>
          </div>

          {/* Trust badges */}
          <div className="mt-8 grid grid-cols-1 gap-3 sm:grid-cols-3">
            {[
              { icon: faFlag, text: 'Seri LUPO tersedia dalam pilihan putih dan hitam' },
              { icon: faSchool, text: 'OREO & TUYAYA: tersedia ukuran SD, SMP, dan SMA' },
              { icon: faBoxesStacked, text: 'Stok dan waktu penyiapan diperiksa sebelum PO' },
            ].map((b) => (
              <div key={b.text} className="flex items-center gap-3 rounded-2xl border border-slate-100 bg-white p-4">
                <span className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg bg-blue-50 text-blue-600">
                  <FontAwesomeIcon icon={b.icon} className="h-4 w-4" />
                </span>
                <p className="text-sm font-medium leading-snug text-slate-600">{b.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Objection handling */}
      <section className="py-14 bg-white">
        <div className="max-w-4xl mx-auto px-4">
          <div className="mb-9 max-w-2xl">
            <h2 className="text-2xl md:text-3xl font-black tracking-tight text-slate-900">Pertanyaan sebelum pengadaan paskibra</h2>
            <p className="mt-2 text-base text-slate-500">Jawaban berikut membahas model, ukuran, MOQ, sample, dokumen, dan pengiriman.</p>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            {OBJECTIONS.map((item) => (
              <div key={item.worry} className="rounded-2xl border border-slate-100 bg-slate-50 p-5">
                <p className="text-base font-bold text-slate-900">{item.worry}</p>
                <div className="mt-2 flex gap-2.5">
                  <FontAwesomeIcon icon={faCheck} className="mt-0.5 h-4 w-4 flex-shrink-0 text-green-600" />
                  <p className="text-base leading-relaxed text-slate-600">{item.answer}</p>
                </div>
              </div>
            ))}
          </div>
          <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-3 rounded-2xl bg-blue-50 border border-blue-100 px-6 py-6 text-center sm:text-left">
            <p className="text-base font-semibold text-slate-700">Kirim kebutuhan sekolah atau instansi kepada admin sebelum menerbitkan PO.</p>
            <WhatsAppButton
              message={softMessage}
              label="Tanyakan kebutuhan pengadaan"
              placement="objection_section"
              className="inline-flex flex-shrink-0 items-center gap-2 rounded-lg bg-green-700 hover:bg-green-800 px-5 py-3 text-sm font-bold text-white transition-colors"
            />
          </div>
        </div>
      </section>

      {/* FAQ */}
      <LpFaqSection faqItems={FAQ_ITEMS} />

      {/* SEO prose — works the target search terms in naturally, without
          false claims (no published unit price, so no price figures here). */}
      <section className="bg-white py-14 border-t border-slate-100">
        <div className="max-w-3xl mx-auto px-4">
          <h2 className="text-xl md:text-2xl font-black tracking-tight text-slate-900">Grosir sarung tangan paskibra untuk sekolah dan instansi</h2>
          <div className="mt-4 space-y-4 text-base leading-relaxed text-slate-600">
            <p>
              Dropify menyuplai <strong className="font-semibold text-slate-800">sarung tangan paskibra</strong> untuk kebutuhan upacara sekolah dan instansi. Pilihan produk mencakup merek, warna, dan ukuran yang berbeda. Kirim jumlah per ukuran agar admin dapat memeriksa komposisi serta menyiapkan quotation.
            </p>
            <p>
              Dropify menyediakan <strong className="font-semibold text-slate-800">sarung tangan paskibra putih</strong> dan <strong className="font-semibold text-slate-800">sarung tangan paskibra hitam</strong> dari LUPO serta OREO. OREO S123 tersedia dalam <strong className="font-semibold text-slate-800">ukuran SD, SMP, dan SMA</strong>. <strong className="font-semibold text-slate-800">Kaos kaki pramuka</strong> hitam TUYAYA tersedia sebagai kategori terpisah. Konfirmasikan MOQ dan susunan ukurannya sebelum order.
            </p>
            <p>
              Pengadaan untuk kegiatan Agustus atau awal tahun ajaran perlu memperhitungkan jumlah, susunan ukuran, waktu penyiapan, dan perjalanan. Kirim jenis, komposisi ukuran, jumlah, kota tujuan, serta target penerimaan melalui WhatsApp. Sekolah, koperasi, atau instansi dapat menyampaikan kebutuhan PO dan invoice pada saat meminta quotation.
            </p>
            <p>
              Halaman ini khusus sarung tangan paskibra dan perlengkapan upacara sekolah. Jika Anda mencari sarung tangan kerja untuk pabrik dan industri, buka halaman{' '}
              <a href="/sarung-tangan-pabrik" className="font-semibold text-blue-600 hover:text-blue-700">
                sarung tangan pabrik
              </a>
              . Kalau mau kulakan sarung tangan kerja buat dijual lagi, ke halaman{' '}
              <a href="/lp/grosir-sarung-tangan" className="font-semibold text-blue-600 hover:text-blue-700">
                grosir sarung tangan
              </a>{' '}
              untuk melihat ketentuan per kategori.
            </p>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="bg-[#0B1120] py-16">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <h2 className="text-2xl md:text-3xl font-black tracking-tight text-white">Minta quotation sesuai jumlah per ukuran</h2>
          <p className="mx-auto mt-3 max-w-xl text-base text-slate-300">
            Untuk meminta penawaran, kirim jenis, komposisi ukuran, jumlah, kota tujuan, dan target penerimaan. Admin akan memeriksa stok serta menyiapkan quotation.
          </p>
          <div className="mt-7 flex justify-center">
            <WhatsAppButton
              message={ctaMessage}
              label="Minta quotation via WhatsApp"
              placement="closing"
              className="inline-flex items-center gap-2.5 rounded-lg bg-green-700 hover:bg-green-800 px-7 py-4 text-base font-bold text-white shadow-lg shadow-green-900/30 transition-colors"
            />
          </div>
          <p className="mt-4 text-sm text-amber-300/90">
            <FontAwesomeIcon icon={faBolt} className="w-3 h-3 mr-1" />
            Konsultasi kebutuhan gratis, tanpa komitmen beli.
          </p>

          {/* Legitimacy: accepted payment methods, reassures a first-time buyer */}
          <div className="mt-9 flex flex-col items-center gap-3 border-t border-white/10 pt-8">
            <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">Menerima pembayaran</p>
            <div className="flex items-center gap-2.5">
              <div className="flex items-center justify-center rounded-lg bg-white px-3" style={{ height: 40 }}>
                <img src="/bca.svg" alt="BCA" width="72" height="22" loading="lazy" decoding="async" className="h-5 w-auto object-contain" />
              </div>
              <div className="flex items-center justify-center rounded-lg bg-white px-3" style={{ height: 40 }}>
                <img src="/mandiri.png" alt="Mandiri" width="80" height="22" loading="lazy" decoding="async" className="h-5 w-auto object-contain" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Cross-links to sibling landing pages */}
      <LpCrossLinks currentPath="/sarung-tangan-paskibra" />

      {/* Footer with legitimacy + contact (no nav links — keeps the funnel tight) */}
      <footer className="border-t border-slate-100 bg-white py-10">
        <div className="max-w-6xl mx-auto px-4 text-center">
          <p className="inline-flex items-center gap-2 text-sm font-black tracking-tight text-slate-900">
            <FontAwesomeIcon icon={faBuilding} className="h-4 w-4 text-blue-600" />
            PT Dropify Distribusi Indonesia
          </p>
          <p className="mt-1.5 text-xs text-slate-500">Pemesanan grosir melalui WhatsApp</p>
          <div className="mt-4 flex flex-wrap items-center justify-center gap-x-5 gap-y-2 text-xs text-slate-600">
            <a
              href={`https://wa.me/${contactPhone.replace(/[^0-9]/g, '')}`}
              {...whatsAppCtaProps('landing_page', { cta_placement: 'footer_phone', lp_category: PRODUCT_NAME })}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 hover:text-green-700 transition-colors"
            >
              <FontAwesomeIcon icon={faWhatsapp} className="h-3.5 w-3.5 text-green-600" />
              {contactPhone}
            </a>
            <a href={`mailto:${contactEmail}`} className="inline-flex items-center gap-1.5 hover:text-blue-700 transition-colors">
              <FontAwesomeIcon icon={faEnvelope} className="h-3.5 w-3.5 text-blue-600" />
              {contactEmail}
            </a>
          </div>
          <div className="mt-5 flex justify-center">
            <SocialLinks variant="light" />
          </div>
          <p className="mt-5 text-xs text-slate-400">© Dropify. Semua hak cipta dilindungi.</p>
          <BrandTrademark className="mt-1 text-xs text-slate-400" />
        </div>
      </footer>
    </div>
  )
}

const PROOF_IMAGE_TRANSFORM = { width: 520, format: 'webp', quality: 70 } as const

// Captions describe exactly what each media item shows (bales hand-marked per
// seri/jenjang, packing, loading), in the API's sort order: 2 photos, 3 videos.
const PROOF_CAPTIONS: Record<'image' | 'video', string[]> = {
  image: [
    'Karungan ditandai per jenjang, dari SD sampai SMA',
    'Dimuat ke armada, langsung berangkat',
  ],
  video: [
    'Bal OREO S123 putih, ditulis seri & ukurannya',
    'Bal-balan disiapkan buat kirim partai besar',
    'Di-press padat biar aman sampai tujuan',
  ],
}
const PROOF_CAPTION_FALLBACK = 'Dokumentasi penyiapan barang di gudang Dropify'

// Not a wall of anonymous screenshots: this section plays the actual warehouse
// footage of paskibra glove bales, which a buyer can't dismiss as staged.
function ProofSection({ media }: { media: ProofMedia[] }) {
  if (media.length === 0) return null

  const counters = { image: 0, video: 0 }
  const cards = media.map((item) => {
    const caption = PROOF_CAPTIONS[item.type][counters[item.type]] ?? PROOF_CAPTION_FALLBACK
    counters[item.type] += 1
    return { ...item, caption }
  })

  return (
    <section className="bg-[#0B1120] py-14 overflow-hidden">
      <div className="max-w-6xl mx-auto px-4">
        <div className="mb-8 max-w-2xl">
          <h2 className="text-2xl md:text-3xl font-black tracking-tight text-white">Penyiapan sarung tangan Paskibra di gudang</h2>
          <p className="mt-2 text-base text-slate-300">
            Label pada bal membantu tim gudang membedakan seri dan jenjang sebelum barang dimuat.
          </p>
        </div>

        {/* Mobile: swipeable snap carousel. Desktop: one row of five. */}
        <div className="-mx-4 flex snap-x snap-mandatory gap-4 overflow-x-auto px-4 pb-2 lg:mx-0 lg:grid lg:grid-cols-5 lg:overflow-visible lg:px-0 lg:pb-0">
          {cards.map((item) => (
            <figure
              key={item.url}
              className="relative w-[62vw] max-w-[250px] flex-shrink-0 snap-center overflow-hidden rounded-2xl bg-slate-900 ring-1 ring-white/10 lg:w-auto lg:max-w-none"
            >
              {item.type === 'video' ? (
                <video
                  src={item.url}
                  className="aspect-[9/16] w-full object-cover"
                  autoPlay
                  muted
                  loop
                  playsInline
                  preload="metadata"
                  aria-label={item.caption}
                />
              ) : (
                <img
                  src={toExternalImage(rawMediaUrl(item.url), PROOF_IMAGE_TRANSFORM)}
                  alt={item.caption}
                  width="520"
                  height="924"
                  loading="lazy"
                  decoding="async"
                  className="aspect-[9/16] w-full object-cover"
                />
              )}
              <span className="absolute left-3 top-3 inline-flex items-center gap-1.5 rounded-full bg-slate-950/70 px-2.5 py-1 text-[11px] font-bold uppercase tracking-wide text-white backdrop-blur-sm">
                {item.type === 'video' ? <FontAwesomeIcon icon={faPlay} className="h-2.5 w-2.5 text-amber-300" /> : <FontAwesomeIcon icon={faCamera} className="h-2.5 w-2.5 text-amber-300" />}
                {item.type === 'video' ? 'Video' : 'Foto'}
              </span>
              <figcaption className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-slate-950/90 via-slate-950/50 to-transparent px-3.5 pb-3.5 pt-10 text-[13px] font-medium leading-snug text-white">
                {item.caption}
              </figcaption>
            </figure>
          ))}
        </div>

        <div className="mt-8 flex flex-col items-center justify-between gap-4 rounded-2xl bg-white/5 px-6 py-5 ring-1 ring-white/10 sm:flex-row">
          <p className="text-center text-base font-semibold text-white sm:text-left">
            Perlu menyiapkan pesanan dalam jumlah besar sebelum jadwal upacara?
          </p>
          <WhatsAppButton
            message={'Halo Dropify, saya membutuhkan sarung tangan paskibra untuk acara pada tanggal ___. Kebutuhan ___ lusin, ukuran ___, dikirim ke ___. Mohon quotation dan informasi waktu penyiapannya.'}
            label="Minta penawaran"
            itemName="Proof Section"
            placement="proof_section"
            className="inline-flex flex-shrink-0 items-center gap-2 rounded-lg bg-green-700 px-5 py-3 text-sm font-bold text-white transition-colors hover:bg-green-800"
          />
        </div>
      </div>
    </section>
  )
}

function LpFaqSection({ faqItems }: { faqItems: FaqItem[] }) {
  const [openIndex, setOpenIndex] = useState<number | null>(null)

  return (
    <section className="bg-slate-50 py-14">
      <div className="max-w-3xl mx-auto px-4">
        <div className="mb-8">
          <h2 className="text-2xl md:text-3xl font-black tracking-tight text-slate-900">Pertanyaan seputar grosir sarung tangan paskibra</h2>
          <p className="mt-2 text-base text-slate-500">Kirim pertanyaan yang belum terjawab kepada admin melalui WhatsApp.</p>
        </div>
        <div className="space-y-2">
          {faqItems.map((item, i) => (
            <div key={i} className="overflow-hidden rounded-xl border border-slate-200 bg-white">
              <button
                type="button"
                onClick={() => {
                  if (openIndex !== i) track('faq_open', { faq_question: item.q, faq_location: 'paskibra' })
                  setOpenIndex(openIndex === i ? null : i)
                }}
                aria-expanded={openIndex === i}
                className="flex w-full items-center justify-between gap-4 px-5 py-4 text-left text-base font-semibold text-slate-800 transition-colors hover:text-blue-600"
              >
                <span>{item.q}</span>
                <FontAwesomeIcon
                  icon={faChevronDown}
                  className={`h-4 w-4 flex-shrink-0 transition-transform duration-200 ${openIndex === i ? 'rotate-180 text-blue-600' : 'text-slate-400'}`}
                />
              </button>
              {openIndex === i && (
                <div className="border-t border-slate-100 px-5 pb-4 pt-3 text-base leading-relaxed text-slate-600">
                  {item.a}
                  {item.link && (
                    <a href={item.link.href} className="mt-2 block font-semibold text-blue-600 hover:text-blue-700">
                      {item.link.label}
                    </a>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}
